import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { StoreDetailResponseType, StoreWithWorktimeType } from '@nailsupply/review-swagger/stores';
import type { StoreConfigTurnType } from '@nailsupply/review-swagger/store-config-turn';
import type { StoreAppointmentConfigType } from '@nailsupply/review-swagger/store-appointment-config';
import type { storePaymentsConfigType } from '@nailsupply/review-swagger/home';
import { SelectedStoreIdKey, StoreTimezoneKey } from '@/consts';

export const useStoreStore = defineStore('storeStore', () => {
  const stores = ref<StoreWithWorktimeType[]>([]);
  const loading = ref(false);
  const storeDetail = ref<StoreDetailResponseType | null>(null);
  const storeAppointmentConfig = ref<StoreAppointmentConfigType | null>(null);
  const storePaymentConfig = ref<storePaymentsConfigType | null>(null);
  const storeTurnConfig = ref<StoreConfigTurnType | null>(null);
  const homeRoute = ref<string | null>(null);

  const selectedStoreId = computed(() => storeDetail.value?.store?._id ?? null);
  const selectedStore = computed(() => storeDetail.value?.store ?? null);
  const requestSameTimeEnabled = computed(() => storeAppointmentConfig.value?.requestSameTimeEnabled ?? false);

  const setStores = (newStores: StoreWithWorktimeType[]) => {
    stores.value = newStores;
  };

  const addStore = (store: StoreWithWorktimeType) => {
    const exists = stores.value.find(s => s._id === store._id);
    if (!exists) {
      stores.value.push(store);
    }
  };

  const updateStore = (storeId: string, storeData: Partial<StoreWithWorktimeType>) => {
    const index = stores.value.findIndex(s => s._id === storeId);
    if (index !== -1) {
      stores.value[index] = { ...stores.value[index], ...storeData } as StoreWithWorktimeType;
    }
  };

  const setLoading = (value: boolean) => {
    loading.value = value;
  };

  const setStoreDetail = (detail: Partial<StoreDetailResponseType> | StoreDetailResponseType | null) => {
    if (detail === null) {
      storeDetail.value = null;
    } else if (storeDetail.value) {
      storeDetail.value = {
        ...storeDetail.value,
        ...detail
      };
    } else {
      storeDetail.value = detail as StoreDetailResponseType;
    }
    localStorage.setItem(SelectedStoreIdKey, storeDetail?.value?.store?._id ?? '');
    localStorage.setItem(StoreTimezoneKey, storeDetail?.value?.store?.timezone ?? '');
  };

  const getStoreById = (storeId: string): StoreWithWorktimeType | null => {
    return stores.value.find(s => s._id === storeId) || null;
  };

  const setStoreTurnConfig = (config: StoreConfigTurnType) => {
    storeTurnConfig.value = config;
  };

  const setStoreAppointmentConfig = (config: StoreAppointmentConfigType) => {
    storeAppointmentConfig.value = config;
  };

  const setStorePaymentConfig = (config: storePaymentsConfigType | null | undefined) => {
    storePaymentConfig.value = config ?? null;
  };

  const setHomeRoute = (route: string | null | undefined) => {
    homeRoute.value = route ?? null;
  };

  const resetStoreScoped = () => {
    storeAppointmentConfig.value = null;
    storePaymentConfig.value = null;
    storeTurnConfig.value = null;
    homeRoute.value = null;
  };

  return {
    // State
    stores,
    selectedStoreId,
    loading,
    storeDetail,
    storeTurnConfig,
    storeAppointmentConfig,
    storePaymentConfig,
    homeRoute,
    requestSameTimeEnabled,
    // Computed
    selectedStore,
    // Actions,
    setStores,
    addStore,
    updateStore,
    setLoading,
    setStoreDetail,
    getStoreById,
    setStoreTurnConfig,
    setStoreAppointmentConfig,
    setStorePaymentConfig,
    setHomeRoute,
    resetStoreScoped,
  };
});
